import React, { useState, useEffect } from "react";
import MainLayout from "../layouts/MainLayout";
import {
  Eye,
  EyeOff,
  User,
  Lock,
  ArrowRight,
  MapPin,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signInWithGooglePopup } from "../lib/googleAuth";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const CDN = process.env.REACT_APP_CDN_URL;
const LOGIN_BG =
  CDN?.trim()
    ? `${CDN.replace(/\/$/, "")}/site/auth/login.jpg`
    : "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1400&q=80";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, hydrated } = useAuth();

  useEffect(() => {
    if (!hydrated || !isAuthenticated) return;
    if (user?.role === "admin") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [hydrated, isAuthenticated, user, navigate]);

  const redirectAfterLogin = (user) => {
    if (user?.mustChangePassword) {
      navigate("/tai-khoan#doi-mat-khau", { replace: true });
      return;
    }
    if (user?.role === "admin") {
      navigate("/admin/dashboard", { replace: true });
      return;
    }
    const from = location.state?.from;
    if (typeof from === "string" && from.startsWith("/") && !from.startsWith("//")) {
      navigate(from, { replace: true });
      return;
    }
    navigate("/", { replace: true });
  };

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(() =>
    location.state?.sessionExpired ? "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại." : ""
  );

  const [formData, setFormData] = useState({
    login: "",
    password: "",
    remember: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const id = formData.login.trim();
    if (!id) {
      setError("Vui lòng nhập email hoặc tên đăng nhập.");
      setSubmitting(false);
      return;
    }

    try {
      const body = { password: formData.password };
      if (id.includes("@")) {
        body.email = id;
      } else {
        body.username = id;
      }
      const data = await authService.login(body);
      login({ token: data.token, refreshToken: data.refreshToken, user: data.user });
      redirectAfterLogin(data.user);
    } catch (err) {
      setError(err.message || "Đăng nhập thất bại. Kiểm tra email/tên đăng nhập hoặc mật khẩu.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setGoogleLoading(true);

    try {
      const { credential: idToken } = await signInWithGooglePopup();
      const data = await authService.googleLogin({ idToken });
      login({ token: data.token, refreshToken: data.refreshToken, user: data.user });
      redirectAfterLogin(data.user);
    } catch (err) {
      setError(err.message || "Đăng nhập bằng Google chưa thành công.");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <MainLayout hideFooter>
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-[#f6efe4] px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-3 sm:py-2.5 md:px-4">
        <div className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-1 flex-col">
          <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] border border-[#d9ccb8] bg-[#fffaf1] shadow-[0_18px_48px_rgba(70,50,30,0.10)] sm:rounded-[28px]">
            <div className="grid h-full min-h-0 flex-1 grid-cols-1 lg:grid-cols-2 lg:grid-rows-1">
              <div className="relative hidden min-h-0 lg:block">
                <img
                  src={LOGIN_BG}
                  alt="Đà Lạt"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(51,67,49,0.34)_0%,rgba(44,54,40,0.70)_55%,rgba(36,44,33,0.86)_100%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,244,220,0.22),transparent_42%)]" />
                <div className="relative z-10 flex h-full min-h-0 flex-col justify-between gap-3 p-5 text-white xl:p-7">
                  <div className="min-w-0">
                    <div className="mb-3 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/12 backdrop-blur-sm sm:h-12 sm:w-12">
                      <MapPin size={20} />
                    </div>

                    <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-white/80">
                      Lòng Vòng Đà Lạt
                    </p>

                    <h1 className="font-faustina text-3xl font-bold italic leading-tight sm:text-4xl xl:text-5xl">
                      Chạm vào
                      <br />
                      Đà Lạt
                    </h1>

                    <p className="mt-4 max-w-md text-sm leading-7 text-white/85 xl:text-[15px]">
                      Lưu lại địa điểm yêu thích, theo dõi hành trình riêng và tiếp tục khám phá
                      thành phố sương mù theo cách thật gần gũi, thật riêng của bạn.
                    </p>
                  </div>

                  <div className="shrink-0 rounded-[20px] border border-[#f1dfbf]/20 bg-black/20 p-4 backdrop-blur-md">
                    <p className="text-sm italic leading-7 text-white/90">
                      “Một tài khoản nhỏ để mở ra cả hành trình lớn.”
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:max-h-full">
                <div className="mx-auto flex h-full min-h-0 w-full max-w-xl flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-6">
                  <div className="shrink-0">
                    <p className="mb-1 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#7f745e]">
                      Welcome back
                    </p>
                    <h2 className="font-faustina text-3xl font-bold tracking-tight text-[#2f352c] sm:text-4xl">
                      Đăng nhập
                    </h2>
                    <p className="mt-2 text-sm leading-7 text-[#5f675b] [@media(max-height:620px)]:hidden">
                      Đăng nhập để tiếp tục khám phá Đà Lạt cùng tài khoản của bạn.
                    </p>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col gap-3 [@media(max-height:620px)]:gap-2">
                    <form
                      onSubmit={handleLogin}
                      className="flex min-h-0 flex-1 flex-col justify-start gap-3 [@media(max-height:620px)]:gap-2"
                    >
                      <div className="min-w-0 shrink-0">
                        <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                          Email hoặc tên đăng nhập
                        </label>
                        <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                          <User size={17} className="shrink-0 text-[#7d776a]" />
                          <input
                            type="text"
                            name="login"
                            value={formData.login}
                            onChange={handleChange}
                            placeholder="VD: admin hoặc email@domain.com"
                            autoComplete="username"
                            className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                            required
                          />
                        </div>
                      </div>

                      <div className="min-w-0 shrink-0">
                        <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                          Mật khẩu
                        </label>
                        <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                          <Lock size={17} className="shrink-0 text-[#7d776a]" />
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Mật khẩu"
                            className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="shrink-0 text-[#7d776a] transition hover:text-[#5f745f]"
                          >
                            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                          </button>
                        </div>
                      </div>

                      <div className="flex shrink-0 flex-col gap-2 text-xs sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:text-sm">
                        <label className="flex min-w-0 items-center gap-2 font-medium text-[#4f5748]">
                          <input
                            type="checkbox"
                            name="remember"
                            checked={formData.remember}
                            onChange={handleChange}
                            className="shrink-0 accent-[#5f745f]"
                          />
                          <span className="leading-snug">Ghi nhớ đăng nhập</span>
                        </label>

                        <Link
                          to="/forgot-password"
                          className="shrink-0 font-bold text-[#5f745f] underline-offset-2 hover:text-[#4f6250] hover:underline sm:text-right"
                        >
                          Quên mật khẩu?
                        </Link>
                      </div>

                      {error ? (
                        <div className="shrink-0 rounded-[18px] border border-[#efc9c9] bg-[#fff3f3] px-3 py-2 text-sm font-medium text-[#a14848]">
                          {error}
                        </div>
                      ) : null}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#5f745f] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(95,116,95,0.28)] ring-1 ring-black/10 transition hover:bg-[#4f6250] disabled:opacity-60 sm:text-base"
                      >
                        {submitting ? "Đang xử lý..." : "Đăng nhập"}
                        <ArrowRight size={16} />
                      </button>
                    </form>

                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                      <div className="h-px flex-1 bg-[#ddd1bf]" />
                      <span className="shrink-0 text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-[#7e7668] sm:text-xs">
                        hoặc
                      </span>
                      <div className="h-px flex-1 bg-[#ddd1bf]" />
                    </div>

                    {/* GIỮ NGUYÊN NÚT GOOGLE */}
                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading}
                      className="inline-flex w-full shrink-0 min-w-0 items-center justify-center gap-2 rounded-lg border border-brand-border bg-[#fffdf8] px-3 py-2.5 text-xs font-semibold text-text-main shadow-soft transition hover:border-accent/60 hover:bg-bg-section/70 hover:shadow-soft-md disabled:opacity-60 sm:rounded-xl sm:text-sm"
                    >
                      <img
                        src="https://www.svgrepo.com/show/475656/google-color.svg"
                        alt="Google"
                        className="h-4 w-4 sm:h-5 sm:w-5"
                      />
                      {googleLoading ? "Đang đăng nhập..." : "Tiếp tục với Google"}
                    </button>
                  </div>

                  <p className="shrink-0 pt-1 text-center text-sm font-medium text-[#5f675b] sm:pt-2">
                    Chưa có tài khoản?{" "}
                    <Link
                      to="/register"
                      className="font-bold text-[#5f745f] underline-offset-2 hover:text-[#4f6250] hover:underline"
                    >
                      Đăng ký ngay
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Login;